import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { ResultsStat } from '../mon-results/results-scan';
import { MonitorCreator } from '../mon-shell/brief-creators';
import { MonPostDrawer } from '../mon-post-drawer/mon-post-drawer';
import {
  ChartDay,
  LeaderRow,
  OVERVIEW_FORMATS,
  OVERVIEW_PERIODS,
  OverviewFormat,
  OverviewMetric,
  OverviewPeriod,
  OverviewQuery,
  compactNumber,
  overviewQueryParams,
  parseOverviewQuery,
  scanOverview,
} from './overview-scan';

const ACCENT = '#DB24BF';
const AXIS_TEXT = '#808080';
const GRID_LINE = 'rgba(128, 128, 128, 0.18)';
/** The empty chart still draws an axis, scaled like a small live brief. */
const EMPTY_AXIS_MAX: Record<OverviewMetric, number> = { views: 40000, engagement: 4000, posts: 4 };

@Component({
  selector: 'mbtm-mon-overview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective, MonPostDrawer],
  templateUrl: './mon-overview.html',
  styleUrl: './mon-overview.scss',
})
export class MonOverview {
  readonly posts = input<ResultsStat[] | null>(null);
  readonly creators = input<MonitorCreator[] | null>(null);
  readonly campaignStart = input<Date | string | null | undefined>(null);
  /** Posts Noodle has found, from the Content tab's count. Some may not have stats yet. */
  readonly postsFound = input<number | null>(null);

  readonly showResults = output<void>();

  readonly formats = OVERVIEW_FORMATS;
  readonly periods = OVERVIEW_PERIODS;
  readonly skeletonRows = [1, 2, 3, 4];

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastNotificationService = inject(ToastNotificationService);
  private readonly now = new Date();

  readonly openedPostId = signal<string | null>(null);
  readonly query = toSignal(this.route.queryParamMap.pipe(map((params) => parseOverviewQuery(params))), {
    initialValue: parseOverviewQuery(this.route.snapshot.queryParamMap),
  });

  readonly loading = computed(() => this.posts() === null || this.creators() === null);

  readonly scan = computed(() =>
    scanOverview({
      posts: this.posts() ?? [],
      creators: this.creators() ?? [],
      query: this.query(),
      campaignStart: parseStart(this.campaignStart()),
      now: this.now,
    }),
  );

  /** Posts are found but none have stats yet, so the empty state shouldn't say we're still waiting for one. */
  readonly awaitingStats = computed(() => this.scan().status === 'empty' && (this.postsFound() ?? 0) > 0);

  readonly chartOptions = computed((): EChartsOption => chartOptions(this.scan().days, this.query().metric, this.scan().status === 'empty'));

  selectMetric(metric: OverviewMetric): void {
    this.updateQuery({ metric });
  }

  selectFormat(format: OverviewFormat): void {
    this.updateQuery({ format });
  }

  onPeriod(event: Event): void {
    this.updateQuery({ period: (event.target as HTMLSelectElement).value as OverviewPeriod });
  }

  openPost(socialPostId: string): void {
    this.openedPostId.set(socialPostId);
  }

  remind(row: LeaderRow): void {
    void this.toastNotificationService.alert({
      title: 'Reminders are coming soon',
      message: `You can't send ${row.name} a reminder from Shout yet.`,
      type: 'info',
      icon: 'info',
    });
  }

  compact(value: number): string {
    return compactNumber(value);
  }

  private updateQuery(patch: Partial<OverviewQuery>): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: overviewQueryParams({ ...this.query(), ...patch }),
      replaceUrl: true,
    });
  }
}

function parseStart(value: Date | string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Line with a creator-coloured dot on each day a post went live; bars for Posts found. */
function chartOptions(days: readonly ChartDay[], metric: OverviewMetric, empty: boolean): EChartsOption {
  const metricName = metric === 'posts' ? 'posts found' : metric;
  const base: EChartsOption = {
    animationDuration: 300,
    grid: { left: 52, right: 12, top: 14, bottom: 30 },
    xAxis: {
      type: 'category',
      boundaryGap: metric === 'posts',
      data: days.map((day) => day.label),
      axisLine: { lineStyle: { color: GRID_LINE } },
      axisTick: { show: false },
      axisLabel: { color: AXIS_TEXT, fontSize: 11, interval: labelInterval(days.length) },
    },
    yAxis: {
      type: 'value',
      splitNumber: 4,
      minInterval: metric === 'posts' ? 1 : 0,
      max: empty ? EMPTY_AXIS_MAX[metric] : metric === 'posts' ? postsAxisMax(days) : undefined,
      axisLabel: { color: AXIS_TEXT, fontSize: 11, formatter: (value: number) => compactNumber(value) },
      splitLine: { lineStyle: { color: GRID_LINE } },
    },
  };

  if (empty) {
    return {
      ...base,
      tooltip: { show: false },
      series: [
        {
          type: 'line',
          data: days.map(() => 0),
          symbol: 'none',
          silent: true,
          lineStyle: { color: '#d9d9d9', width: 2, type: [6, 6] },
        },
      ],
    };
  }

  return {
    ...base,
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#12062B',
      borderWidth: 0,
      padding: [9, 12],
      textStyle: { color: '#fff', fontSize: 12 },
      axisPointer: { type: metric === 'posts' ? 'shadow' : 'line', lineStyle: { type: 'dashed', color: 'rgba(128, 128, 128, 0.6)' } },
      formatter: (params: unknown) => {
        const index = (Array.isArray(params) ? params[0] : params) as { dataIndex: number };
        return tooltipHtml(days[index.dataIndex], metric, metricName);
      },
    },
    series: [
      metric === 'posts'
        ? {
            type: 'bar',
            data: days.map((day) => day.value),
            barMaxWidth: 16,
            itemStyle: { color: ACCENT, borderRadius: [2, 2, 0, 0] },
          }
        : {
            type: 'line',
            data: days.map((day) => ({
              value: day.value,
              symbolSize: day.posts.length ? 10 : 0,
              itemStyle: day.posts.length ? { color: day.posts[0].color, borderColor: '#fff', borderWidth: 2 } : undefined,
            })),
            symbol: 'circle',
            showSymbol: true,
            // Narrow charts would otherwise thin out the dots, and each one marks a post.
            showAllSymbol: true,
            lineStyle: { color: ACCENT, width: 2.5 },
            itemStyle: { color: ACCENT },
            emphasis: { scale: false },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(219, 36, 191, 0.2)' },
                  { offset: 1, color: 'rgba(219, 36, 191, 0)' },
                ],
              },
            },
          },
    ],
  };
}

/** Posts found counts in whole posts: at least 4, in steps of 4, as in the design. */
function postsAxisMax(days: readonly ChartDay[]): number {
  const peak = Math.max(0, ...days.map((day) => day.value));
  return Math.max(4, Math.ceil(peak / 4) * 4);
}

/** About seven date labels across the axis, as in the design's ~90px spacing. */
function labelInterval(count: number): number {
  return Math.max(0, Math.ceil(count / 7) - 1);
}

function tooltipHtml(day: ChartDay | undefined, metric: OverviewMetric, metricName: string): string {
  if (!day) {
    return '';
  }
  const posts = day.posts
    .map(
      (post) =>
        `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">` +
        `<i style="width:8px;height:8px;border-radius:50%;background:${post.color}"></i>` +
        `${escapeHtml(post.creatorName)} posted a ${escapeHtml(post.formatLabel)}</div>`,
    )
    .join('');
  const divider = posts ? `<div style="margin-top:5px;border-top:1px solid rgba(255,255,255,.14)"></div>` : '';
  return (
    `<span style="color:rgba(255,255,255,.7)">${escapeHtml(day.longLabel)}</span>` +
    `<b style="display:block;font-size:20px;font-weight:800;line-height:1;margin:3px 0 1px">${metric === 'posts' ? day.value : compactNumber(day.value)}</b>` +
    `<span style="color:rgba(255,255,255,.7)">${metricName}</span>${divider}${posts}`
  );
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char);
}
